// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import NavbarComponent from "./components/Navbar";
import Footer from "./pages/Footer";

import Home from "./pages/Home";
import PresentationPage from "./pages/PresentationPage";
import About from "./pages/About";
import Login from "./pages/Login";
import Register from "./pages/Register";

import AddMedication from "./components/AddMedication";
import MedicationList from "./components/MedicationList";
import RequestMedication from "./components/RequestMedication";
import MedicationRequests from "./components/MedicationRequests";
import RequestDetails from "./components/RequestDetails";
import DonorProfile from "./components/DonorProfile";
import PrivateRoute from "./components/PrivateRoute";
import UserRequests from "./components/UserRequests";
import DonorNotifications from "./components/DonorNotifications";

// Noticias
import AdminNews from "./components/AdminNews";
import NewsFeed from "./components/NewsFeed";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { Spinner } from "react-bootstrap";
import { normalizeRole, isDonorRole, isSuperadminRole } from "./utils/roles";

/** Decide la pantalla inicial según rol */
const LandingRouter = ({ user }) => {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRole = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const rawRole = snap.exists() ? snap.data().role : "requester";
        setRole(normalizeRole(rawRole) || "requester");
      } catch {
        setRole("requester");
      } finally {
        setLoading(false);
      }
    };
    loadRole();
  }, [user?.uid]);

  if (!user) return <PresentationPage />;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  // SUPERADMIN: envía a panel de noticias admin
  if (isSuperadminRole(role)) return <Navigate to="/admin/noticias" replace />;

  // Donante: su bandeja
  if (isDonorRole(role)) return <DonorNotifications />;

  // Solicitante: Home (Noticias + medicamentos)
  return <Home />;
};

/** Wrapper para rutas solo superadmin */
const RequireSuperadmin = ({ children }) => {
  const [user] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setRole(null);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        setRole(snap.exists() ? normalizeRole(snap.data().role) : null);
      } catch {
        setRole(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user?.uid]);

  if (!user) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (!isSuperadminRole(role)) return <Navigate to="/" replace />;
  return children;
};

const App = () => {
  const [user, loading] = useAuthState(auth);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="d-flex flex-column min-vh-100">
        <NavbarComponent />
        <main className="flex-grow-1">
          <Routes>
            {/* Raíz: decide según rol */}
            <Route index element={<LandingRouter user={user} />} />

            {/* Públicas */}
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
            <Route path="/register" element={!user ? <Register /> : <Navigate to="/" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/noticias" element={<NewsFeed />} />

            {/* Privadas */}
            <Route element={<PrivateRoute />}>
              <Route path="/home" element={<Home />} />
              <Route path="/add-medication" element={<AddMedication />} />
              <Route path="/medications" element={<MedicationList />} />
              <Route path="/request-medication" element={<RequestMedication />} />
              <Route path="/request-medication/:medicationId" element={<RequestMedication />} />
              <Route path="/my-requests" element={<UserRequests />} />
              <Route path="/solicitudes" element={<MedicationRequests />} />
              <Route path="/solicitudes/:requestId" element={<RequestDetails />} />
              <Route path="/received-requests" element={<DonorNotifications />} />

              {/* Solo superadmin */}
              <Route
                path="/admin/noticias"
                element={
                  <RequireSuperadmin>
                    <AdminNews />
                  </RequireSuperadmin>
                }
              />
              <Route path="/donor-profile" element={<DonorProfile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
